import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';

const parseMaxHeightPx = (menuMaxHeightClass) => {
  if (menuMaxHeightClass.includes('70vh') || menuMaxHeightClass.includes('32rem')) return 512;
  if (menuMaxHeightClass.includes('28rem')) return 448;
  if (menuMaxHeightClass.includes('24rem')) return 384;
  if (menuMaxHeightClass.includes('max-h-60')) return 240;
  if (menuMaxHeightClass.includes('max-h-48')) return 192;
  return 320;
};

const MultiSelect = ({
    label,
    value = [],
    onChange,
    options = [],
    placeholder = "Select options",
    className = "",
    menuMaxHeightClass = 'max-h-60',
    error,
    required = false,
    disabled = false,
    onScrollEnd,
    loadingMore = false,
    size = 'md',
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [menuStyle, setMenuStyle] = useState({});
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const searchInputRef = useRef(null);

    const preferredMaxHeight = parseMaxHeightPx(menuMaxHeightClass);

    const isCompact = size === 'sm';

    const computeMenuStyle = useCallback(() => {
        if (!triggerRef.current) return;

        const rect = triggerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom - 12;
        const spaceAbove = rect.top - 12;
        const openUp = spaceBelow < 220 && spaceAbove > spaceBelow;
        const available = openUp ? spaceAbove : spaceBelow;
        const maxHeight = Math.min(preferredMaxHeight, Math.max(180, available - 8));
        const minMenuWidth = isCompact ? 260 : 320;

        setMenuStyle({
            position: 'fixed',
            left: rect.left,
            width: Math.max(rect.width, minMenuWidth),
            zIndex: 10000,
            maxHeight,
            ...(openUp
                ? { bottom: viewportHeight - rect.top + 6 }
                : { top: rect.bottom + 6 }),
        });
    }, [preferredMaxHeight, isCompact]);

    const openMenu = () => {
        if (disabled) return;
        computeMenuStyle();
        setIsOpen(true);
    };

    const closeMenu = useCallback(() => {
        setIsOpen(false);
        setSearchTerm('');
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (triggerRef.current?.contains(event.target)) return;
            if (menuRef.current?.contains(event.target)) return;
            closeMenu();
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [closeMenu]);

    useEffect(() => {
        if (!isOpen) return;

        const update = () => computeMenuStyle();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);

        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [isOpen, computeMenuStyle]);

    useEffect(() => {
        if (!isOpen) return;
        const focusInput = () => {
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            }
        };
        focusInput();
        const raf = window.requestAnimationFrame(focusInput);
        const timer = setTimeout(focusInput, 30);
        return () => {
            window.cancelAnimationFrame(raf);
            clearTimeout(timer);
        };
    }, [isOpen]);

    const filteredOptions = options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSelect = (optionValue) => {
        let newValue;
        if (value.includes(optionValue)) {
            newValue = value.filter(v => v !== optionValue);
        } else {
            newValue = [...value, optionValue];
        }
        onChange(newValue);
    };

    const handleSelectAll = () => {
        const filteredValues = filteredOptions.map(o => o.value);
        const allFilteredSelected = filteredValues.every(v => value.includes(v));

        let newValue;
        if (allFilteredSelected) {
            newValue = value.filter(v => !filteredValues.includes(v));
        } else {
            newValue = [...new Set([...value, ...filteredValues])];
        }
        onChange(newValue);
    };

    const removeValue = (e, valToRemove) => {
        e.stopPropagation();
        onChange(value.filter(v => v !== valToRemove));
    };

    const getSelectedLabel = () => {
        const placeholderClass = isCompact ? 'text-gray-500 text-sm' : 'text-gray-500';
        const valueClass = isCompact ? 'text-gray-800 text-sm' : 'text-gray-900';
        const chipClass = isCompact
            ? 'inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700'
            : 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800';
        const countChipClass = isCompact
            ? 'text-gray-800 font-medium bg-gray-100 px-2 py-0.5 rounded text-xs'
            : 'text-gray-900 font-medium bg-indigo-100 px-2 py-0.5 rounded text-xs text-indigo-800';

        if (value.length === 0) return <span className={placeholderClass}>{placeholder}</span>;

        if (value.length === 1) {
            const option = options.find(o => o.value === value[0]);
            return <span className={`${valueClass} truncate`}>{option ? option.label : value[0]}</span>;
        }

        return (
            <div className="flex flex-wrap gap-1">
                {value.length <= 3 ? (
                    value.map(v => {
                        const option = options.find(o => o.value === v);
                        return (
                            <span key={v} className={chipClass}>
                                {option ? option.label.split('(')[0].trim() : v}
                                <XMarkIcon
                                    className={`ml-0.5 cursor-pointer hover:text-gray-900 ${isCompact ? 'h-2.5 w-2.5' : 'h-3 w-3 hover:text-indigo-900'}`}
                                    onClick={(e) => removeValue(e, v)}
                                />
                            </span>
                        );
                    })
                ) : (
                    <span className={countChipClass}>
                        {value.length} selected
                    </span>
                )}
            </div>
        );
    };

    const allFilteredSelected = filteredOptions.length > 0 && filteredOptions.every(o => value.includes(o.value));
    const selectAllLabel = searchTerm.trim()
      ? (allFilteredSelected
        ? `Deselect all matching (${filteredOptions.length})`
        : `Select all matching (${filteredOptions.length})`)
      : (allFilteredSelected
        ? `Deselect all (${filteredOptions.length})`
        : `Select all (${filteredOptions.length})`);

    const menu = isOpen ? (
        <div
            ref={menuRef}
            style={menuStyle}
            className="bg-white border border-gray-200 rounded-xl shadow-xl shadow-gray-200/70 ring-1 ring-black/5 overflow-hidden flex flex-col"
        >
            <div className="p-2 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                <div className="relative mb-2">
                    <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                        ref={searchInputRef}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search categories..."
                        className={`w-full pl-8 pr-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                            isCompact ? 'py-1 text-xs' : 'py-1.5 text-sm'
                        }`}
                    />
                </div>
            </div>

            <div
                className="overflow-y-auto flex-1 min-h-0 p-1"
                onScroll={onScrollEnd ? (e) => {
                    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
                    if (scrollHeight - scrollTop - clientHeight < 60) onScrollEnd();
                } : undefined}
            >
                {filteredOptions.length === 0 && !loadingMore ? (
                    <div className="px-3 py-4 text-sm text-gray-500 text-center">No results found</div>
                ) : (
                    <>
                        <button
                            type="button"
                            onClick={handleSelectAll}
                            className={`w-full flex items-center gap-2.5 font-medium rounded mb-1 border-b border-gray-100 ${
                                isCompact ? 'px-2 py-1.5 text-xs' : 'px-3 py-2.5 text-sm'
                            } ${allFilteredSelected ? 'bg-indigo-50 text-indigo-900' : 'text-gray-800 hover:bg-gray-50'}`}
                        >
                            <div className={`
                                flex-shrink-0 w-4 h-4 border rounded flex items-center justify-center transition-colors
                                ${allFilteredSelected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300 bg-white'}
                            `}>
                                {allFilteredSelected && <CheckIcon className="w-3 h-3 text-white" />}
                            </div>
                            <span>{selectAllLabel}</span>
                        </button>
                        {filteredOptions.map(option => {
                        const isSelected = value.includes(option.value);
                        return (
                            <div
                                key={option.value}
                                onClick={() => handleSelect(option.value)}
                                className={`
                                    flex items-center rounded cursor-pointer mb-0.5
                                    ${isCompact ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-sm'}
                                    ${isSelected ? 'bg-indigo-50 text-indigo-900' : 'text-gray-700 hover:bg-gray-50'}
                                `}
                            >
                                <div className={`
                                    flex-shrink-0 w-4 h-4 border rounded mr-3 flex items-center justify-center transition-colors
                                    ${isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300 bg-white'}
                                `}>
                                    {isSelected && <CheckIcon className="w-3 h-3 text-white" />}
                                </div>
                                <span className="truncate">{option.label}</span>
                            </div>
                        );
                    })}
                    </>
                )}
                {loadingMore && (
                    <div className="flex items-center justify-center gap-2 py-3 text-xs text-gray-400">
                        <svg className="animate-spin h-3 w-3 text-indigo-500" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                        Loading more…
                    </div>
                )}
            </div>
        </div>
    ) : null;

    return (
        <div className={className}>
            {label && (
                <label className={`block font-medium text-gray-700 mb-1 ${isCompact ? 'text-xs' : 'text-sm'}`}>
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
            )}
            <div ref={triggerRef} className="relative">
                <div
                    onClick={() => (isOpen ? closeMenu() : openMenu())}
                    className={`
                        w-full bg-white border text-left cursor-pointer transition-all
                        focus:outline-none focus:ring-2 focus:ring-indigo-500
                        ${isCompact
                            ? 'rounded border-gray-300 px-2 py-1.5 text-sm hover:border-indigo-400 min-h-0'
                            : 'min-h-[38px] rounded-xl px-3 py-2'}
                        ${error ? 'border-red-300' : (isCompact ? 'border-gray-300' : 'border-gray-200')}
                        ${disabled ? 'bg-gray-50 cursor-not-allowed opacity-75' : (isCompact ? '' : 'hover:border-gray-300')}
                    `}
                >
                    <div className="flex items-center justify-between gap-1.5">
                        <div className="flex-1 min-w-0 truncate">
                            {getSelectedLabel()}
                        </div>
                        <ChevronDownIcon className={`text-gray-400 transition-transform flex-shrink-0 ${isCompact ? 'h-3.5 w-3.5' : 'h-4 w-4'} ${isOpen ? 'rotate-180' : ''}`} />
                    </div>
                </div>
            </div>
            {typeof document !== 'undefined' && menu && createPortal(menu, document.body)}
            {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
        </div>
    );
};

export default MultiSelect;

